<template>
  <div class="auth-page">
    <div class="auth-hero">
      <div class="auth-copy">
        <p class="eyebrow">Office Supply Hub</p>
        <h1>办公用品申领系统</h1>
        <p class="hero-text">
          围绕企业级用品管理、员工申领审批、库存预警补货和站内信通知构建的一体化工作台。
        </p>
        <div class="demo-list">
          <div class="demo-card">
            <span>超级管理员联系方式</span>
            <strong>微信号：zy544726003</strong>
            <p>如需开通企业、初始化账号或处理系统权限，请联系平台超级管理员统一处理。</p>
          </div>
          <div class="demo-card">
            <span>系统单位信息</span>
            <strong>上海市质量监督检验技术研究院有限公司</strong>
            <p>本系统用于办公用品申领、审批、库存预警、补货流转与消息通知的统一管理。</p>
          </div>
        </div>
      </div>
    </div>

    <div class="auth-panel">
      <el-card shadow="hover" class="auth-card">
        <el-segmented v-model="activeTab" :options="tabOptions" class="auth-segment" />

        <el-form
          v-if="activeTab === 'login'"
          ref="loginFormRef"
          :model="loginForm"
          :rules="loginRules"
          label-position="top"
          class="auth-form"
        >
          <el-form-item label="登录账号" prop="username">
            <el-input v-model="loginForm.username" placeholder="请输入账号" />
          </el-form-item>
          <el-form-item label="登录密码" prop="password">
            <el-input v-model="loginForm.password" type="password" show-password placeholder="请输入密码" />
          </el-form-item>
          <el-form-item label="验证码" prop="captcha">
            <div class="captcha-row">
              <el-input v-model="loginForm.captcha" placeholder="请输入验证码" class="captcha-input" />
              <div class="captcha-img-wrap" @click="fetchCaptcha">
                <img v-if="!captchaLoading && captchaImg" :src="captchaImg" class="captcha-img" alt="验证码" />
                <el-icon v-if="captchaLoading" class="captcha-spin"><Loading /></el-icon>
              </div>
            </div>
          </el-form-item>
          <el-button type="primary" :loading="loading" class="submit-btn" @click="handleLogin">
            登录系统
          </el-button>
        </el-form>

        <el-form
          v-else
          ref="registerFormRef"
          :model="registerForm"
          :rules="registerRules"
          label-position="top"
          class="auth-form"
        >
          <el-form-item label="登录账号" prop="username">
            <el-input v-model="registerForm.username" placeholder="设置登录账号" />
          </el-form-item>
          <el-form-item label="登录密码" prop="password">
            <el-input v-model="registerForm.password" type="password" show-password placeholder="设置密码" />
          </el-form-item>
          <el-form-item label="确认登录密码" prop="confirmPassword">
            <el-input
              v-model="registerForm.confirmPassword"
              type="password"
              show-password
              placeholder="请再次输入登录密码"
            />
          </el-form-item>
          <el-form-item label="企业邀请码" prop="companyCode">
            <el-input v-model="registerForm.companyCode" placeholder="注册必须填写正确的邀请码" />
          </el-form-item>
          <el-form-item label="验证码" prop="captcha">
            <div class="captcha-row">
              <el-input v-model="registerForm.captcha" placeholder="请输入验证码" class="captcha-input" />
              <div class="captcha-img-wrap" @click="fetchCaptcha">
                <img v-if="!captchaLoading && captchaImg" :src="captchaImg" class="captcha-img" alt="验证码" />
                <el-icon v-if="captchaLoading" class="captcha-spin"><Loading /></el-icon>
              </div>
            </div>
          </el-form-item>
          <el-button type="primary" :loading="loading" class="submit-btn" @click="handleRegister">
            注册员工账号
          </el-button>
        </el-form>
      </el-card>
    </div>

    <footer v-if="showWebsiteFooter" class="auth-footer">
      <span>© {{ currentYear }} 上海艾莉亚网络科技有限公司 版权所有</span>
      <span class="footer-separator">|</span>
      <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer">
        沪ICP备2021028632号-2
      </a>
      <span class="footer-separator">|</span>
      <a href="https://dxzhgl.miit.gov.cn" target="_blank" rel="noopener noreferrer">
        增值电信业务许可证：沪B2-20250747
      </a>
    </footer>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import { login, register, getCaptcha } from '../api/system'
import { hasValidSession, normalizeSessionPayload } from '../utils/session'

export default {
  name: 'AuthShell',
  emits: ['authenticated'],
  components: { Loading },
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请再次输入登录密码'))
        return
      }

      if (value !== this.registerForm.password) {
        callback(new Error('两次输入的密码不一致'))
        return
      }

      callback()
    }

    return {
      activeTab: 'login',
      loading: false,
      captchaImg: '',
      captchaUuid: '',
      captchaLoading: false,
      tabOptions: [
        { label: '登录', value: 'login' },
        { label: '员工注册', value: 'register' }
      ],
      loginForm: {
        username: '',
        password: '',
        captcha: ''
      },
      registerForm: {
        username: '',
        password: '',
        confirmPassword: '',
        companyCode: '',
        captcha: ''
      },
      loginRules: {
        username: [{ required: true, message: '请输入登录账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入登录密码', trigger: 'blur' }],
        captcha: [{ required: true, message: '请输入验证码', trigger: 'blur' }]
      },
      registerRules: {
        username: [{ required: true, message: '请输入登录账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入登录密码', trigger: 'blur' }],
        confirmPassword: [{ validator: validateConfirmPassword, trigger: 'blur' }],
        companyCode: [{ required: true, message: '请输入企业邀请码', trigger: 'blur' }],
        captcha: [{ required: true, message: '请输入验证码', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.fetchCaptcha()
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    showWebsiteFooter() {
      return typeof window !== 'undefined' && !window.electronAPI
    }
  },
  methods: {
    async fetchCaptcha() {
      this.captchaLoading = true
      try {
        const response = await getCaptcha()
        const data = response.data || response
        if (data.img) {
          this.captchaImg = 'data:image/jpeg;base64,' + data.img
        } else {
          this.captchaImg = ''
        }
        this.captchaUuid = data.uuid || ''
      } catch (error) {
        this.captchaImg = ''
        this.captchaUuid = ''
      } finally {
        this.captchaLoading = false
      }
    },
    async handleLogin() {
      const valid = await this.$refs.loginFormRef.validate().catch(() => false)

      if (!valid) {
        return
      }

      this.loading = true

      try {
        const response = await login({ ...this.loginForm, uuid: this.captchaUuid })
        const session = normalizeSessionPayload(response)

        if (!hasValidSession(session)) {
          throw new Error('登录响应缺少登录凭证，请检查后端 token 返回')
        }

        if (session.user.role === undefined || session.user.role === null || session.user.role === '') {
          throw new Error('登录响应缺少用户角色信息，请检查后端 user.role 返回')
        }

        ElMessage.success(response.msg || response.message || '登录成功')
        this.fetchCaptcha()
        this.$emit('authenticated', session)
      } catch (error) {
        ElMessage.error(error.message)
      } finally {
        this.loading = false
      }
    },
    async handleRegister() {
      const valid = await this.$refs.registerFormRef.validate().catch(() => false)

      if (!valid) {
        return
      }

      this.loading = true

      try {
        const response = await register({
          username: this.registerForm.username,
          password: this.registerForm.password,
          companyCode: this.registerForm.companyCode,
          captcha: this.registerForm.captcha,
          uuid: this.captchaUuid
        })
        ElMessage.success(response.message)
        this.loginForm.username = this.registerForm.username
        this.loginForm.password = this.registerForm.password
        this.registerForm = {
          username: '',
          password: '',
          confirmPassword: '',
          companyCode: '',
          captcha: ''
        }
        this.fetchCaptcha()
        this.activeTab = 'login'
      } catch (error) {
        ElMessage.error(error.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  background:
    radial-gradient(circle at top left, rgba(15, 23, 42, 0.75), transparent 30%),
    linear-gradient(135deg, #0f172a 0%, #1d3557 42%, #e2e8f0 42%, #f8fafc 100%);
}

.auth-hero,
.auth-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.auth-copy {
  max-width: 540px;
  color: #e2e8f0;
}

.eyebrow {
  margin: 0 0 16px;
  font-size: 13px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #93c5fd;
  font-weight: 700;
}

.auth-copy h1 {
  margin: 0;
  font-size: 54px;
  line-height: 1.08;
}

.hero-text {
  margin: 24px 0 32px;
  font-size: 17px;
  line-height: 1.9;
  color: rgba(226, 232, 240, 0.82);
}

.demo-list {
  display: grid;
  gap: 14px;
}

.demo-card {
  padding: 18px 20px;
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.26);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(148, 163, 184, 0.24);
}

.demo-card span {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  color: #cbd5e1;
}

.demo-card strong {
  display: block;
  color: #f8fafc;
}

.demo-card p {
  margin: 10px 0 0;
  color: rgba(226, 232, 240, 0.78);
  font-size: 13px;
  line-height: 1.8;
}

.auth-card {
  width: 100%;
  max-width: 480px;
  border: none;
  border-radius: 28px;
  padding: 12px;
}

.auth-segment {
  width: 100%;
  margin-bottom: 24px;
  --el-segmented-bg-color: #e2e8f0;
  --el-segmented-item-selected-bg-color: #1d4e89;
  --el-segmented-item-selected-color: #f8fafc;
  --el-border-radius-base: 16px;
}

.auth-segment :deep(.el-segmented) {
  width: 100%;
  padding: 6px;
  border-radius: 18px;
  background: #e2e8f0;
}

.auth-segment :deep(.el-segmented__group) {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.auth-segment :deep(.el-segmented__item) {
  justify-content: center;
  min-height: 44px;
  border-radius: 14px;
  color: #334155;
  font-weight: 600;
  transition: all 0.2s ease;
}

.auth-segment :deep(.el-segmented__item.is-selected) {
  color: #f8fafc;
  background: #1d4e89;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
}

.auth-segment :deep(.el-segmented__item-label) {
  line-height: 1;
}

.auth-form {
  display: grid;
  gap: 4px;
}
.captcha-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.captcha-input {
  flex: 1;
}

.captcha-img-wrap {
  flex-shrink: 0;
  width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s ease;
}



.captcha-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.captcha-spin {
  animation: captcha-rotate 0.8s linear infinite;
  font-size: 20px;
  color: #94a3b8;
}

@keyframes captcha-rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}


.submit-btn {
  width: 100%;
  margin-top: 8px;
  height: 46px;
  border-radius: 14px;
  --el-button-bg-color: #1d4e89;
  --el-button-border-color: #1d4e89;
  --el-button-hover-bg-color: #163a66;
  --el-button-hover-border-color: #163a66;
  --el-button-active-bg-color: #0f2a4a;
  --el-button-active-border-color: #0f2a4a;
}

.auth-footer {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 24px 28px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.8;
  flex-wrap: wrap;
  text-align: center;
}

.auth-footer a {
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;
}

.auth-footer a:hover {
  color: #1d4e89;
}

.footer-separator {
  color: #94a3b8;
}

@media (max-width: 1024px) {
  .auth-page {
    grid-template-columns: 1fr;
  }

  .auth-hero {
    padding-bottom: 0;
  }

  .auth-copy h1 {
    font-size: 38px;
  }
}

@media (max-width: 640px) {
  .auth-hero,
  .auth-panel {
    padding: 20px 16px;
  }

  .auth-copy h1 {
    font-size: 30px;
  }

  .auth-footer {
    gap: 6px;
    padding: 4px 16px 20px;
    font-size: 12px;
  }
}
</style>
